<!--
 * @Descripttion: 
 * @version: 图片上传组件
 * @Author: dengyao
 * @Date: 2023-11-28 15:23:45
-->
<template>
  <Upload
    list-type="picture-card"
    className="avatar-uploader"
    v-model:file-list="fileList"
    :headers="state.upLoadHeaders"
    :action="state.upLoadUrl"
    :beforeUpload="beforeUpload"
    @change="handleChange"
    @preview="handlePreview"
    accept=".png,.jpg,.jpeg"
    :maxCount="maxCount"
  >
    <Spin v-if="fileList && fileList.length < props.maxCount" :spinning="state.loading">
      <div>
        <PlusOutlined></PlusOutlined>
        <div class="ant-upload-text">{{ $lang('上传图片') }}</div>
      </div>
    </Spin>
  </Upload>
  <AImage
    :width="200"
    :style="{ display: 'none' }"
    :preview="{
      visible: imageState.visible,
      onVisibleChange: setVisible
    }"
    :src="imageState.imageUrl"
  />
</template>

<script lang="ts" setup>
  import { reactive, ref } from 'vue'
  import { Image as AImage, Upload, Spin, message } from 'ant-design-vue'
  import { PlusOutlined } from '@ant-design/icons-vue'
  import type { UploadProps } from 'ant-design-vue'
  import { isSuccess } from '@/utils/index'
  import { useAuthStore } from '@/store/auth'
  import { lang } from '@/utils/index'
  const authStore = useAuthStore()
  const { VITE_APP_API_BASE_URL } = import.meta.env
  const imageState = ref({
    visible: false,
    imageUrl: ''
  })

  interface Props {
    imageSize: number
    maxCount?: number
    uploadUrl?: string
    watermarkFn?: (file: File) => Promise<File>
  }
  const props = withDefaults(defineProps<Props>(), {
    maxCount: 1,
    imageSize: 10
  })
  interface StateIn extends Record<string, any> {
    loading: boolean
    upLoadHeaders: { [key: string]: any }
    upLoadUrl: string
  }
  const state: StateIn = reactive({
    loading: false,
    upLoadHeaders: {
      Authtoken: authStore.token,
      sysCode: 'WEB'
    },
    upLoadUrl: props.uploadUrl || `${VITE_APP_API_BASE_URL}/image/uploadImage`
  })

  const beforeUpload = (file: File): Promise<File | string> => {
    return new Promise((resolve, reject) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
      if (!isJpgOrPng) {
        message.error(lang('只能上传JPG或PNG格式文件!'))
        resolve(Upload.LIST_IGNORE)
        return
      }

      const imageSize = props.imageSize
      const isLt2MB = file.size / 1024 / 1024 > imageSize
      if (isLt2MB) {
        message.error(lang(`图片大小必须小于${imageSize}MB!`))
        resolve(Upload.LIST_IGNORE)
        return
      }
      if (props.watermarkFn) {
        props.watermarkFn(file).then((res) => {
          resolve(res)
        }).catch((err) => {
          resolve(Upload.LIST_IGNORE)
        })
      } else {
        resolve(file)
      } 
    })
  }

  const fileList = ref<UploadProps['fileList']>([])
  const emit = defineEmits(['update:value'])

  const handleChange = (info: any) => {
    console.log('handleChange', info)
    if (info.file.status === 'done') {
      let response = info.file.response
      state.loading = false
      if (!isSuccess(response)) {
        message.error(response.msg || lang('上传出错'))
        info.file.status = 'error'
        return
      }
    }
    if (info.file.status === 'error') {
      state.loading = false
      message.error(lang('上传出错'))
      return
    }

    const str = [] as string[]
    fileList.value?.forEach(v => {
      if (v?.response && (v.response as any).data) {
        // 上传返回
        str.push((v.response as any).data)
      } else if (v.url) {
        // 自定义编辑/回显
        str.push(v.name)
      }
    })
    console.log('str', str)
    emit('update:value', str.join(','))
  }

  /**
   * @description: 预览图片开关
   * @param {*} value
   * @return {*}
   */
  const setVisible = (value: boolean): void => {
    imageState.value.visible = value
  }

  /**
   * @description: 预览图片
   * @param {*} file
   * @return {*}
   */
  const handlePreview = async (file: any): Promise<any> => {
    if (!file.url && !file.preview) {
      file.preview = (await getBase64(file.originFileObj)) as string
    }
    imageState.value.imageUrl = file.url || file.preview
    setVisible(true)
  }

  /**
   * @description: 将 File 对象转换为 Base64 编码的字符串
   * @param {File} file - 需要转换为 Base64 编码的 File 对象
   * @return {Promise<string>} - 返回一个 Promise，当转换成功时 resolve Base64 编码的字符串，失败时 reject 错误信息
   */
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (error) => reject(error)
    })
  }

  const clearFile = () => {
    fileList.value = []
    emit('update:value', '')
  }

  defineExpose({
    clearFile
  })
</script>